import { ReadonlyVec2, vec2, vec3 } from 'gl-matrix';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import {
    fmmSignedDistanceMm,
    quantizeSdfMmToUint8,
} from '~/engine/util/algorithms/sdf/SDFPropagation';
import { trilinearSample } from '~/engine/util/algorithms/sdf/trilinearSample';

const SPREAD_BASE_MM = 1;
const SPREAD_DIAMETER_FACTOR = 1;

/** Mutable state for an active SDF-native deform stroke. */
export class DeformActionState {
    private readonly structure: RegionOfInterest;
    private readonly view: PlaneView;
    private readonly diameter: number;
    private readonly textureWrite: TextureWrite;

    private sliceZ: number | undefined;
    /** Original quantized SDF slice (for cancel). */
    private originalSlice: Uint8Array | undefined;

    private workingSdf: Uint8Array | undefined;
    private size: vec2;
    private readonly res: vec2;

    /** Anchor in voxel coordinates, or undefined if not yet captured. */
    private anchorVoxel: vec2 | undefined;

    /** Mouse position when the anchor was captured (mm). */
    private strokeStartMouse: vec2;
    /** Current mouse position (mm), updated each frame. */
    private currentMouse: vec2;
    /** Last warped SDF (Uint8) for reinit at finish. */
    private lastWarped: Uint8Array | undefined;

    public constructor(
        structure: RegionOfInterest,
        image: ImageObject,
        view: PlaneView,
        startCoordinates: ReadonlyVec2,
        diameter: number
    ) {
        this.structure = structure;
        this.view = view;
        this.diameter = diameter;

        const patientPos = view.clientToPatientCoordinates(startCoordinates);
        this.strokeStartMouse = vec2.fromValues(patientPos[0], patientPos[1]);
        this.currentMouse = vec2.clone(this.strokeStartMouse);

        this.textureWrite = structure.startModify('deform');

        const texture = structure.texture;
        this.size = vec2.create();
        this.res = vec2.fromValues(1, 1);

        if (texture && !texture.isEmpty) {
            const grid = texture.textureGrid;
            vec2.set(this.size, grid.sizeX, grid.sizeY);
            vec2.set(this.res, grid.resX, grid.resY);

            const centerVoxel = grid.toVoxelSpace(patientPos);
            const sliceZ = Math.floor(centerVoxel[2]);
            const sdf = texture.signedDistanceField;

            if (sliceZ >= 0 && sliceZ < sdf.size[2]) {
                this.sliceZ = sliceZ;

                // Snapshot original slice for cancel and
                // as the sampling source during the stroke.
                const sliceData = sdf.sliceToArray(sliceZ);
                this.originalSlice = new Uint8Array(sliceData);
                this.workingSdf = sliceData;

                // Try to find anchor immediately.
                this.findAnchor();
            }
        }
    }

    /** Gaussian sigma derived from brush diameter. */
    private get gamma(): number {
        return Math.max(
            1e-6,
            SPREAD_BASE_MM + SPREAD_DIAMETER_FACTOR * this.diameter
        );
    }

    public get targetStructure(): RegionOfInterest {
        return this.structure;
    }

    public onMouseMove(coordinates: ReadonlyVec2): EventHandlerResult {
        const patientPos = this.view.clientToPatientCoordinates(coordinates);
        vec2.set(this.currentMouse, patientPos[0], patientPos[1]);
        if (!this.anchorVoxel) {
            this.findAnchor();
        }
        this.applyDeformation();
        this.view.renderLayers();
        ViewManager.viewUpdateEvent(this.createContourUpdateEvent());
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /** Commit the deformed SDF: reinitialize with DEFAULT_MARGIN. */
    public finish(): void {
        const texture = this.structure.texture;
        if (
            texture &&
            !texture.isEmpty &&
            this.sliceZ !== undefined &&
            this.lastWarped
        ) {
            const threshold = ContourTexture.SDF_THRESHOLD;
            const defaultMargin = ContourTexture.DEFAULT_MARGIN;
            // Reinitialize FMM from the warped Uint8 SDF
            // and re-quantize with the standard falloff.
            const fmm = fmmSignedDistanceMm(
                this.lastWarped,
                this.size[0],
                this.size[1],
                1,
                threshold,
                this.res[0],
                this.res[1],
                1,
                defaultMargin
            );
            const quantized = quantizeSdfMmToUint8(
                fmm,
                this.size[0],
                this.size[1],
                1,
                threshold,
                defaultMargin
            );
            texture.signedDistanceField.arrayToTextureSlice(
                quantized[0],
                this.sliceZ
            );
        }
        this.textureWrite.finish();
    }

    /** Restore the original SDF slice (Escape). */
    public cancel(): void {
        if (this.originalSlice && this.sliceZ !== undefined) {
            const texture = this.structure.texture;
            if (texture && !texture.isEmpty) {
                texture.signedDistanceField.arrayToTextureSlice(
                    this.originalSlice,
                    this.sliceZ
                );
            }
        }
        this.textureWrite.cancel();
    }

    /**
     * Find the closest surface texel within the brush
     * radius. Uses the FMM distance field: the texel
     * with the smallest absolute distance is closest
     * to the surface.
     */
    private findAnchor(): boolean {
        if (!this.workingSdf || !this.originalSlice || this.anchorVoxel) {
            return false;
        }

        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return false;
        }

        const grid = texture.textureGrid;
        const halfDiameter = this.diameter / 2;

        // Convert mouse to voxel space.
        const mouseVoxel = grid.toVoxelSpace(
            vec3.fromValues(this.currentMouse[0], this.currentMouse[1], 0)
        );
        const mvx = mouseVoxel[0];
        const mvy = mouseVoxel[1];

        // Brush radius in voxels.
        const searchRadius = halfDiameter / Math.min(this.res[0], this.res[1]);

        const xMin = Math.max(0, Math.floor(mvx - searchRadius));
        const xMax = Math.min(this.size[0] - 1, Math.ceil(mvx + searchRadius));
        const yMin = Math.max(0, Math.floor(mvy - searchRadius));
        const yMax = Math.min(this.size[1] - 1, Math.ceil(mvy + searchRadius));

        let bestAbsDist = Infinity;
        let bestVx = -1;
        let bestVy = -1;

        for (let vy = yMin; vy <= yMax; vy++) {
            for (let vx = xMin; vx <= xMax; vx++) {
                const dxV = (vx - mvx) * this.res[0];
                const dyV = (vy - mvy) * this.res[1];
                const distSq = dxV * dxV + dyV * dyV;
                if (distSq > halfDiameter * halfDiameter) {
                    continue;
                }

                // Closest to surface = closest to threshold.
                const absDist = Math.abs(
                    this.workingSdf[vy * this.size[0] + vx] -
                        ContourTexture.SDF_THRESHOLD
                );
                if (absDist < bestAbsDist) {
                    bestAbsDist = absDist;
                    bestVx = vx;
                    bestVy = vy;
                }
            }
        }

        // Only accept if we found a texel reasonably
        // close to the surface. Scale tolerance by the
        // working falloff so it represents a fixed mm
        // distance regardless of Uint8 encoding range.
        const toleranceMm = halfDiameter;
        const toleranceUint8 =
            (toleranceMm * ContourTexture.SDF_THRESHOLD) /
            ContourTexture.DEFAULT_MARGIN;
        if (bestVx < 0 || bestAbsDist > toleranceUint8) {
            return false;
        }

        this.anchorVoxel = vec2.fromValues(bestVx, bestVy);
        vec2.copy(this.strokeStartMouse, this.currentMouse);
        return true;
    }

    private applyDeformation(): void {
        if (!this.workingSdf || !this.anchorVoxel) {
            return;
        }

        const texture = this.structure.texture;
        if (!texture || texture.isEmpty || this.sliceZ === undefined) {
            return;
        }

        // Pre-compute per-frame constants.
        const sx = this.currentMouse[0] - this.strokeStartMouse[0];
        const sy = this.currentMouse[1] - this.strokeStartMouse[1];
        const sMagSq = sx * sx + sy * sy;

        if (sMagSq < 1e-12) {
            return;
        }

        const sdf = this.workingSdf;
        const sizeX = this.size[0];
        const sizeY = this.size[1];
        const resX = this.res[0];
        const resY = this.res[1];
        const anchorX = this.anchorVoxel[0];
        const anchorY = this.anchorVoxel[1];
        const gammaSq = this.gamma * this.gamma;
        const svx = sx / resX;
        const svy = sy / resY;

        // Bounding box: Gaussian drops below 0.01 at
        // d ≈ 3σ. Expand by stroke length.
        const reach = this.gamma * 3;
        const strokeMag = Math.sqrt(sMagSq);
        const padX = (reach + strokeMag) / resX;
        const padY = (reach + strokeMag) / resY;
        const vxMin = Math.max(0, Math.floor(anchorX - padX));
        const vxMax = Math.min(sizeX - 1, Math.ceil(anchorX + svx + padX));
        const vyMin = Math.max(0, Math.floor(anchorY - padY));
        const vyMax = Math.min(sizeY - 1, Math.ceil(anchorY + svy + padY));

        // Copy only the affected region from the original.
        const result = new Uint8Array(sdf);

        for (let vy = vyMin; vy <= vyMax; vy++) {
            for (let vx = vxMin; vx <= vxMax; vx++) {
                const weight = this.computeDeformWeight(
                    vx,
                    vy,
                    sx,
                    sy,
                    sMagSq,
                    gammaSq
                );
                if (weight < 0.01) {
                    continue;
                }

                const srcValue = trilinearSample(
                    sdf,
                    sizeX,
                    sizeY,
                    1,
                    vx - svx * weight,
                    vy - svy * weight,
                    0
                );

                result[vy * sizeX + vx] = Math.round(
                    Math.max(0, Math.min(255, srcValue))
                );
            }
        }

        this.lastWarped = result;

        texture.signedDistanceField.arrayToTextureSlice(result, this.sliceZ);
    }

    /**
     * Compute the deform weight for a texel.
     * Gaussian falloff on distance from the stroke segment.
     */
    private computeDeformWeight(
        vx: number,
        vy: number,
        sx: number,
        sy: number,
        sMagSq: number,
        gammaSq: number
    ): number {
        const dxMm = (vx - this.anchorVoxel![0]) * this.res[0];
        const dyMm = (vy - this.anchorVoxel![1]) * this.res[1];
        const dot = dxMm * sx + dyMm * sy;
        const segT = Math.max(0, Math.min(1, dot / sMagSq));
        const sdx = dxMm - segT * sx;
        const sdy = dyMm - segT * sy;

        return Math.exp(-(sdx * sdx + sdy * sdy) / (2 * gammaSq));
    }

    private createContourUpdateEvent(): VolumeViewUpdateEvent {
        return new VolumeViewUpdateEvent(
            this.structure.boundingBox,
            LayerBit.CONTOUR_LAYER_BIT,
            true
        );
    }
}
/**
 * Manages the lifecycle of an SDF-native deform stroke.
 */
export class DeformAction {
    private readonly structure: RegionOfInterest;
    private readonly image: ImageObject;
    private readonly diameter: number;

    public constructor(
        structure: RegionOfInterest,
        image: ImageObject,
        diameter: number
    ) {
        this.structure = structure;
        this.image = image;
        this.diameter = diameter;
    }

    public startAction(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): DeformActionState {
        return new DeformActionState(
            this.structure,
            this.image,
            view,
            coordinates,
            this.diameter
        );
    }

    public stopAction(state: DeformActionState): EventHandlerResult {
        state.finish();
        const bb = state.targetStructure.boundingBox;
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(bb, LayerBit.CONTOUR_LAYER_BIT, true)
        );
    }
}
